import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Wrench, Plus, MapPin, Phone, Pencil, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import DynamicFields from "@/components/DynamicFields";
import api from "@/lib/apiClient";

const emptyForm = { name: "", contact_person: "", phone: "", email: "", address: "", city: "", pincode: "" };

export default function ServiceCenters() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [custom, setCustom] = useState({});
  const [edit, setEdit] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await api.get("/service-centers")).data); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name || !form.city || !form.pincode) { toast.error("Name, city and pincode required."); return; }
    try {
      await api.post("/service-centers", { ...form, custom });
      toast.success("Service center added.");
      setOpen(false); setForm(emptyForm); setCustom({}); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed."); }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/service-centers/${edit.id}`, {
        name: edit.name, contact_person: edit.contact_person, phone: edit.phone,
        email: edit.email, address: edit.address, city: edit.city, pincode: edit.pincode,
      });
      toast.success("Service center updated.");
      setEdit(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed."); }
  };

  const del = async (id) => {
    try { await api.delete(`/service-centers/${id}`); toast.success("Service center deleted."); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed."); }
  };

  return (
    <div className="space-y-6" data-testid="service-centers-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold">Service Centers</h1>
          <p className="text-muted-foreground mt-1">Manage service centers and providers for warranty servicing.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="add-center-button"><Plus className="h-4 w-4" /> Add Center</Button>
          </DialogTrigger>
          <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="add-center-dialog">
            <DialogHeader><DialogTitle className="font-display">Add Service Center</DialogTitle>
              <DialogDescription>Create a service center and optionally link a provider.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5 col-span-2"><Label>Name *</Label><Input value={form.name} onChange={set("name")} data-testid="center-name-input" /></div>
              <div className="space-y-1.5"><Label>Contact Person</Label><Input value={form.contact_person} onChange={set("contact_person")} /></div>
              <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} /></div>
              <div className="space-y-1.5 col-span-2"><Label>Provider Email (link to service provider account)</Label><Input value={form.email} onChange={set("email")} /></div>
              <div className="space-y-1.5 col-span-2"><Label>Address</Label><Input value={form.address} onChange={set("address")} /></div>
              <div className="space-y-1.5"><Label>City *</Label><Input value={form.city} onChange={set("city")} data-testid="center-city-input" /></div>
              <div className="space-y-1.5"><Label>Pincode *</Label><Input value={form.pincode} onChange={set("pincode")} data-testid="center-pincode-input" /></div>
              <div className="col-span-2"><DynamicFields entity="service_center" values={custom} onChange={(k, v) => setCustom((c) => ({ ...c, [k]: v }))} /></div>
            </div>
            <DialogFooter><Button onClick={create} data-testid="confirm-center-button">Add Center</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <p className="text-muted-foreground">Loading...</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map((c) => (
            <Card key={c.id} className="p-5 hover:shadow-md hover:-translate-y-[2px] transition-all" data-testid={`center-card-${c.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><Wrench className="h-5 w-5 text-primary" /></div>
                  <div className="min-w-0">
                    <h3 className="font-display font-bold truncate">{c.name}</h3>
                    <p className="text-xs text-muted-foreground">{c.contact_person}</p>
                  </div>
                </div>
                <div className="flex gap-0.5">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEdit({ ...c })} data-testid={`edit-center-${c.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" data-testid={`delete-center-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader><AlertDialogTitle>Delete {c.name}?</AlertDialogTitle>
                        <AlertDialogDescription>This permanently removes the service center.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => del(c.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
              <div className="mt-4 space-y-1.5 text-sm text-muted-foreground">
                <p className="flex items-center gap-2"><MapPin className="h-4 w-4" /> {c.city}, {c.pincode}</p>
                <p className="flex items-center gap-2"><Phone className="h-4 w-4" /> {c.phone || "—"}</p>
              </div>
              {c.owner_user_id && <span className="mt-3 inline-block text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5">Provider linked</span>}
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="edit-center-dialog">
          <DialogHeader><DialogTitle className="font-display">Edit {edit?.name}</DialogTitle>
            <DialogDescription>Update service center details.</DialogDescription>
          </DialogHeader>
          {edit && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5 col-span-2"><Label>Name</Label><Input value={edit.name || ""} onChange={(e) => setEdit((f) => ({ ...f, name: e.target.value }))} data-testid="edit-center-name" /></div>
              <div className="space-y-1.5"><Label>Contact Person</Label><Input value={edit.contact_person || ""} onChange={(e) => setEdit((f) => ({ ...f, contact_person: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Phone</Label><Input value={edit.phone || ""} onChange={(e) => setEdit((f) => ({ ...f, phone: e.target.value }))} /></div>
              <div className="space-y-1.5 col-span-2"><Label>Provider Email</Label><Input value={edit.email || ""} onChange={(e) => setEdit((f) => ({ ...f, email: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>City</Label><Input value={edit.city || ""} onChange={(e) => setEdit((f) => ({ ...f, city: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Pincode</Label><Input value={edit.pincode || ""} onChange={(e) => setEdit((f) => ({ ...f, pincode: e.target.value }))} /></div>
            </div>
          )}
          <DialogFooter><Button onClick={saveEdit} data-testid="save-center-edit">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
